/**
 * Allocating a stratified sample across its strata.
 *
 * - **Proportional**: n_h = n × N_h / N — every stratum sampled at the same rate, so the
 *   sample is self-weighting.
 * - **Equal**: n_h = n / H — the same number from every stratum, for comparing strata.
 * - **Neyman (optimal)**: n_h = n × N_h S_h / Σ N_k S_k (Neyman, 1934) — the allocation
 *   that minimises the variance of the stratified mean for a fixed n (Cochran, 1977, §5.5).
 *
 * Two practical rules sit on top. A method that asks for more units than a stratum holds
 * takes the whole stratum (a census) and re-shares the rest among the others. And whole
 * numbers come from the largest-remainder (Hamilton) method: each stratum gets the whole
 * part of its quota, and the units still unplaced go to the largest fractional parts, ties
 * to the larger stratum — so the sizes always add up to exactly n.
 *
 * The standard error of the stratified mean is Cochran's (1977, eq. 5.6) with the finite
 * population correction: SE² = Σ W_h² × (S_h² / n_h) × (1 − n_h / N_h), W_h = N_h / N.
 */

import { parseNumberInput } from "@/lib/parse-number";
import { sum } from "@/lib/stats/descriptive";
import {
  type Delimiter,
  detectDelimiter,
  isMissingToken,
  splitLine,
} from "@/lib/tool-kit/research-design/matrix";

export interface Stratum {
  name: string;
  /** N_h, the stratum's population size. */
  size: number;
  /** S_h, the standard deviation of the study variable within the stratum, if known. */
  sd: number | null;
}

export type AllocationMethod = "proportional" | "equal" | "neyman";

const LINE_BREAK = /\r\n|\r|\n/;

export interface ParsedStrata {
  strata: Stratum[];
  /** 1-based line numbers that could not be read as a stratum. */
  skipped: number[];
  /** The first line was a header row ("Stratum, N, SD") and was skipped. */
  header: boolean;
}

/** Name, size and SD from one line's cells. */
function fieldsOf(cells: string[], delimiter: Delimiter): string[] {
  if (delimiter !== "space") {
    return [cells[0] ?? "", cells[1] ?? "", cells[2] ?? ""];
  }
  // Space-separated names may themselves contain spaces: read numbers from the right.
  const numbers: string[] = [];
  let end = cells.length;
  while (
    end > 1 &&
    numbers.length < 2 &&
    parseNumberInput(cells[end - 1]) !== null
  ) {
    numbers.unshift(cells[end - 1]);
    end -= 1;
  }
  return [cells.slice(0, end).join(" "), numbers[0] ?? "", numbers[1] ?? ""];
}

/** One stratum per line: name, population size, SD (optional). */
export function parseStrata(text: string): ParsedStrata {
  const lines = text.split(LINE_BREAK);
  const filled = lines.filter((line) => line.trim() !== "");
  const delimiter = detectDelimiter(filled);
  const strata: Stratum[] = [];
  const skipped: number[] = [];
  let header = false;
  let seenFirst = false;
  for (const [index, line] of lines.entries()) {
    if (line.trim() === "") {
      continue;
    }
    const [name, sizeText, sdText] = fieldsOf(
      splitLine(line, delimiter),
      delimiter
    );
    const size = parseNumberInput(sizeText);
    const isFirst = !seenFirst;
    seenFirst = true;
    if (size === null && isFirst) {
      header = true;
      continue;
    }
    const sd = isMissingToken(sdText) ? null : parseNumberInput(sdText);
    const sdUnreadable = !isMissingToken(sdText) && (sd === null || sd < 0);
    if (size === null || !Number.isInteger(size) || size < 1 || sdUnreadable) {
      skipped.push(index + 1);
      continue;
    }
    strata.push({ name, size, sd });
  }
  return { strata, skipped, header };
}

/** How much of n each method gives each stratum, before any capping. */
export function allocationWeights(
  strata: readonly Stratum[],
  method: AllocationMethod
): number[] | null {
  if (method === "equal") {
    return strata.map(() => 1);
  }
  if (method === "proportional") {
    return strata.map((stratum) => stratum.size);
  }
  if (strata.some((stratum) => stratum.sd === null)) {
    return null;
  }
  const weights = strata.map((stratum) => stratum.size * (stratum.sd ?? 0));
  return sum(weights) > 0 ? weights : null;
}

/**
 * Real-valued quotas that respect each stratum's size: a stratum asked for more than it
 * holds is taken whole and the remainder re-shared among the rest, until none is over.
 */
export function cappedQuotas(
  weights: readonly number[],
  caps: readonly number[],
  n: number
): { quotas: number[]; census: boolean[] } {
  const quotas = weights.map(() => 0);
  const census = weights.map(() => false);
  let remaining = n;
  let open = weights.map((_, h) => h);
  let changed = true;
  while (changed && open.length > 0) {
    changed = false;
    let total = sum(open.map((h) => weights[h]));
    // Only zero-weight strata are left but units are not: share them by size instead.
    const shareBy = total > 0 ? weights : caps;
    total = total > 0 ? total : sum(open.map((h) => caps[h]));
    for (const h of open) {
      quotas[h] = (remaining * shareBy[h]) / total;
    }
    const over = open.filter((h) => quotas[h] > caps[h] + 1e-9);
    if (over.length > 0) {
      for (const h of over) {
        quotas[h] = caps[h];
        census[h] = true;
        remaining -= caps[h];
      }
      open = open.filter((h) => !census[h]);
      changed = true;
    }
  }
  return { quotas, census };
}

/**
 * Hamilton's largest-remainder rounding: whole parts first, then one unit at a time to the
 * largest fractional parts, ties to the higher `priority` (then the earlier stratum).
 */
export function largestRemainder(
  quotas: readonly number[],
  total: number,
  caps: readonly number[],
  priority: readonly number[]
): number[] {
  const whole = quotas.map((quota, h) =>
    Math.min(caps[h], Math.floor(quota + 1e-9))
  );
  const order = quotas
    .map((quota, h) => ({
      h,
      fraction: Math.max(0, quota - Math.floor(quota + 1e-9)),
    }))
    .sort(
      (a, b) =>
        b.fraction - a.fraction || priority[b.h] - priority[a.h] || a.h - b.h
    )
    .map((entry) => entry.h);
  let left = total - sum(whole);
  let placed = true;
  while (left > 0 && placed) {
    placed = false;
    for (const h of order) {
      if (left > 0 && whole[h] < caps[h]) {
        whole[h] += 1;
        left -= 1;
        placed = true;
      }
    }
  }
  return whole;
}

export interface AllocatedStratum {
  /** The exact (unrounded) quota after capping. */
  quota: number;
  /** n_h, a whole number; the n_h sum to n. */
  sample: number;
  /** n_h / N_h. */
  fraction: number;
  /** N_h / n_h — the population units each sampled unit stands for; null when n_h = 0. */
  weight: number | null;
  /** Every unit in the stratum is sampled. */
  census: boolean;
  /** The method asked for more units than the stratum holds; the excess was re-shared. */
  capped: boolean;
}

export type AllocationProblem =
  | "too-few-strata"
  | "sample-too-small"
  | "sample-exceeds-population"
  | "sd-needed";

export type AllocationOutcome =
  | { ok: true; strata: AllocatedStratum[]; standardError: number | null }
  | { ok: false; problem: AllocationProblem };

export function allocate(
  strata: readonly Stratum[],
  n: number,
  method: AllocationMethod
): AllocationOutcome {
  if (strata.length < 2) {
    return { ok: false, problem: "too-few-strata" };
  }
  if (!Number.isInteger(n) || n < 1) {
    return { ok: false, problem: "sample-too-small" };
  }
  const caps = strata.map((stratum) => stratum.size);
  if (n > sum(caps)) {
    return { ok: false, problem: "sample-exceeds-population" };
  }
  const weights = allocationWeights(strata, method);
  if (!weights) {
    return { ok: false, problem: "sd-needed" };
  }
  const { quotas, census } = cappedQuotas(weights, caps, n);
  const samples = largestRemainder(quotas, n, caps, caps);
  return {
    ok: true,
    strata: strata.map((stratum, h) => ({
      quota: quotas[h],
      sample: samples[h],
      fraction: samples[h] / stratum.size,
      weight: samples[h] > 0 ? stratum.size / samples[h] : null,
      census: census[h] || samples[h] === stratum.size,
      capped: census[h],
    })),
    standardError: stratifiedStandardError(strata, samples),
  };
}

/** SE of the stratified mean; null without every SD, or when a varied stratum gets no units. */
export function stratifiedStandardError(
  strata: readonly Stratum[],
  samples: readonly number[]
): number | null {
  const population = sum(strata.map((stratum) => stratum.size));
  let variance = 0;
  for (const [h, stratum] of strata.entries()) {
    if (stratum.sd === null) {
      return null;
    }
    const n = samples[h];
    if (n === 0) {
      if (stratum.sd > 0) {
        return null;
      }
      continue;
    }
    const w = stratum.size / population;
    variance +=
      ((w * w * stratum.sd * stratum.sd) / n) * (1 - n / stratum.size);
  }
  return Math.sqrt(variance);
}
